import Input from 'components/common/material-form/Input';
import RaisedButton from 'components/common/material/RaisedButton';
import LoadingBlocking from 'components/loading/LoadingBlocking';
import { autobind } from 'core-decorators';
import PropTypes from 'prop-types';
import React from 'react';
import CSSModules from 'react-css-modules';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';
import { connect } from 'react-redux';
import { Field, formValueSelector, reduxForm } from 'redux-form';
import {ValidationsUtils} from 'utils/validationUtils';
import {normalizeSsn, normalizeSsnLast4Digits} from '../../utils/normalizeSsn';
import styles from '../auth.scss';
import * as staticComponents from '../authWithOtp/staticComponents2';
import * as authConstants from "./constants2";
import { TooManyAttempts } from './TooManyAttempts';


const selector = formValueSelector('forgotPassword');

function validate(values, props) {
  const {intl: {formatMessage}, isFullSsnValidationRequired} = props;
  const errors = {};

  if (!values.email) {
    errors.email = formatMessage({id: 'registration.field.label.emailErrorMissing'});
  }
  else if (!ValidationsUtils.validateEmail(values.email)) {
    errors.email = formatMessage({id: 'registration.field.label.emailError'});
  }

  let ssn4DigitsErrorId;

  if (isFullSsnValidationRequired) {
    if (!values.ssn4Digits) {
      ssn4DigitsErrorId = 'registration.field.label.ssnErrorMissing';
    } else if (values.ssn4Digits.length < 9) {
      ssn4DigitsErrorId = 'registration.field.label.ssnError';
    }
  } else {
    if (!values.ssn4Digits) {
      ssn4DigitsErrorId = 'registration.field.label.ssn4DigitsErrorMissing';
    } else if (values.ssn4Digits.length < 4) {
      ssn4DigitsErrorId = 'registration.field.label.ssn4DigitsError';
    }
  }
  
  if (ssn4DigitsErrorId) {
    errors.ssn4Digits = formatMessage({id: ssn4DigitsErrorId});
  }

  return errors;
}

@injectIntl
@connect(
  state => ({
    email: selector(state, 'email'),
    ssn4Digits: selector(state, 'ssn4Digits'),
    isLoadingFPRequest: state.authRoot.forgotPassword.isLoadingFPRequest,
    isFPRequestFail: state.authRoot.forgotPassword.isFPRequestFail,
    blockedTimeLeft: state.authRoot.forgotPassword.blockedTimeLeft,
  })
)
@reduxForm({
  form: 'forgotPassword',
  validate
})
@CSSModules(styles, {allowMultiple: true})
class ForgotPasswordForm extends React.Component {

  static propTypes = {
    intl: intlShape,
    submitCallback: PropTypes.func.isRequired,
    failReason: PropTypes.string,
    ssn4Digits: PropTypes.string,
    email: PropTypes.string,
    isLoadingFPRequest: PropTypes.bool,
    isFPRequestFail: PropTypes.bool,
    invalid: PropTypes.bool.isRequired,
    isFullSsnValidationRequired: PropTypes.bool,
    redirect: PropTypes.func.isRequired,
    clearRequestFailure: PropTypes.func.isRequired,
    blockedTimeLeft: PropTypes.number,
  };

  constructor(props) {
    super(props);

    this.state = {
      notCostumerVisible: false
    };
  }

  @autobind
  handleSubmit() {
    const {ssn4Digits, email} = this.props;
    this.props.submitCallback(ssn4Digits, email);
  }

  @autobind
  toggleNotCostumerDialog() {
    this.setState({notCostumerVisible: !this.state.notCostumerVisible});
  }

  renderFooter() {
    const footerValues = {
      signIn: staticComponents.loginLink
    };

    return (
      <div styleName="footer">
        <FormattedMessage id="forgotPassword.remember" values={footerValues}/>
      </div>
    );
  }

  renderFail() {
    const failValues = {
      tryAgainLink: <a onClick={this.props.clearRequestFailure}><FormattedMessage id="common.tryAgain" /></a>,
      contactUsLink: staticComponents.contactUsLink
    };

    return (
      <div styleName="content forgotPassNotAccount">
        <div styleName="title" data-at="failureTitle"><FormattedMessage id="auth.title.notFound"/></div>
        <br/>
        <div styleName="subtitle" data-at="failureSubitle">
          <FormattedMessage id="auth.message.notFound"/>
          <br/>
          <FormattedMessage id="auth.message.tryAgain" values={failValues}/>
        </div>
      </div>
    );
  }

  isAccountNotRegistered() {
    const {isFPRequestFail, failReason} = this.props;
    if (isFPRequestFail && failReason === authConstants.ACCOUNT_NOT_REGISTERED) {
      return true;
    }
  }

  renderAccountNotRegistered() {
    const failValues = {
      signUp: staticComponents.signUp,
      contactUsLink: staticComponents.contactUsLink
    };

    return (
      <div styleName="content forgotPassNotAccount">
        <div styleName="title" data-at="failureTitle"><FormattedMessage id="auth.title.notFound"/></div>
        <br/>
        <div styleName="subtitle" data-at="failureSubitle">
          <FormattedMessage id="auth.message.notFound"/>
          <br/>
          <FormattedMessage id="auth.message.tryAgainNotRegistered" values={failValues}/>
        </div>
      </div>
    );
  }

  isGenericError() {
    const {isFPRequestFail, failReason} = this.props;
    if (isFPRequestFail && failReason !== authConstants.ACCOUNT_NOT_REGISTERED && failReason !== authConstants.FAIL_REASON_NOT_FOUND) {
      return true;
    }
  }
  
  isBlocked() {
    return this.props.blockedTimeLeft;
  }

  render() {
    const {intl: {formatMessage}, isFPRequestFail, failReason, isFullSsnValidationRequired} = this.props;

    if (this.isBlocked()) {
      return <TooManyAttempts timeLeft={this.props.blockedTimeLeft} onTimerEnd={()=>this.props.clearRequestFailure()} />;
    }

    if (isFPRequestFail && failReason === authConstants.FAIL_REASON_NOT_FOUND) {
      return this.renderFail();
    }

    if (this.isAccountNotRegistered()) {
      return this.renderAccountNotRegistered();
    }

    return (
      <div styleName="forgotPasswordContent">
        {this.props.isLoadingFPRequest && <LoadingBlocking /> }
        {
          this.isGenericError() && <div data-at={'genericError'} styleName="noAccount">
            <FormattedMessage id="common.technicalError"/>
          </div>
        }
        { 
          isFullSsnValidationRequired && <div data-at={'fullSsnRequiredMessage'} styleName="noAccount">
            <FormattedMessage id="passwordUpdated.error.fullSSNRequired"/>
          </div> 
        }
        <div data-at="regTitle" styleName="forgotPasswordTitle"><FormattedMessage id="forgotPassword.title.reset"/></div>
          <div styleName="forgotPasswordSubTitle" data-at="subtitle">
            <FormattedMessage id="forgotPassword.instructions"/>
          </div>
        <div>
          <Field data-at="emailInput" name="email" component={Input} label={formatMessage({id: 'registration.field.label.email'})} fullWidth={true}/>
        </div>
        <div>
          {isFullSsnValidationRequired && 
            <Field data-at="ssn4DigitsInput" name="ssn4Digits" component={Input} label={formatMessage({id: 'registration.field.label.ssn'})} fullWidth={true} normalize={normalizeSsn}/>
          }
          {!isFullSsnValidationRequired && 
            <Field data-at="ssn4DigitsInput" name="ssn4Digits" component={Input} label={formatMessage({id: 'registration.field.label.ssn4Digits'})} fullWidth={true} normalize={normalizeSsnLast4Digits}/>
          }
        </div>
        <div>
          <RaisedButton data-at="continueForgotPassword" onClick={this.handleSubmit} disabled={this.props.invalid} fullWidth={true} primary={true} label={formatMessage({id: 'forgotPassword.button.continue'})}/>
        </div>
        <br/>
        {this.renderFooter()}
      </div>
    );

  }
}
export default ForgotPasswordForm;



// WEBPACK FOOTER //
// ./src/auth/authWithOtp/ForgotPasswordForm2.js